import { useState } from "react";
import type { AppInfo } from "../../data/apps";

export const display = { fontFamily: "'Fredoka', 'Nunito', sans-serif" };
export const body = { fontFamily: "'Nunito', sans-serif" };

/** App icon that gracefully falls back to a gradient tile with an emoji
 *  until you drop the real icon file into /public. */
export function AppIcon({
  app,
  size = 64,
}: {
  app: Pick<AppInfo, "icon" | "iconEmoji" | "gradient" | "name">;
  size?: number;
}) {
  const [failed, setFailed] = useState(false);
  const radius = size * 0.23; // Apple-style squircle-ish corner
  if (!app.icon || failed) {
    return (
      <div
        aria-hidden
        className="flex items-center justify-center shrink-0"
        style={{
          width: size,
          height: size,
          borderRadius: radius,
          fontSize: size * 0.5,
          background: `linear-gradient(135deg, ${app.gradient[0]}, ${app.gradient[1]})`,
        }}
      >
        {app.iconEmoji}
      </div>
    );
  }
  return (
    <img
      src={app.icon}
      alt={`${app.name} app icon`}
      width={size}
      height={size}
      className="shrink-0 object-cover"
      style={{ borderRadius: radius }}
      onError={() => setFailed(true)}
    />
  );
}

/** 0–5 star row, supports halves via width clipping. */
export function Stars({ value, size = 16 }: { value: number; size?: number }) {
  return (
    <span
      className="relative inline-block leading-none align-middle"
      role="img"
      aria-label={`${value} out of 5 stars`}
      style={{ fontSize: size, color: "#D8D2C8" }}
    >
      <span aria-hidden>★★★★★</span>
      <span
        aria-hidden
        className="absolute left-0 top-0 overflow-hidden whitespace-nowrap"
        style={{ width: `${(value / 5) * 100}%`, color: "#FF9F1C" }}
      >
        ★★★★★
      </span>
    </span>
  );
}

/** Phone-shaped screenshot frame. Shows a dashed placeholder until the
 *  image exists in /public. */
export function Screenshot({
  src,
  alt,
  accent,
}: {
  src: string;
  alt: string;
  accent: string;
}) {
  const [failed, setFailed] = useState(false);
  return (
    <div
      className="shrink-0 w-[200px] md:w-[230px] aspect-[9/19.5] rounded-[2rem] border-[6px] border-[#241633] bg-[#241633] overflow-hidden snap-center"
      style={{ boxShadow: "0 18px 40px -18px rgba(36, 22, 51, 0.5)" }}
    >
      {failed ? (
        <div
          className="w-full h-full flex flex-col items-center justify-center gap-2 rounded-[1.6rem] border-2 border-dashed"
          style={{ borderColor: `${accent}66`, background: "#FFF9F2" }}
        >
          <span aria-hidden style={{ fontSize: 28 }}>
            📱
          </span>
          <span className="text-xs text-center px-4" style={{ color: "#8A7F70" }}>
            Screenshot
            <br />
            coming soon
          </span>
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover rounded-[1.6rem]"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}

/** The signature: "ha"s drifting through the hero like laughter in the air. */
export function FloatingHas() {
  const has = [
    { t: "HA", x: "6%", y: "18%", s: 34, r: -14, o: 0.5, d: 0 },
    { t: "ha", x: "16%", y: "68%", s: 22, r: 10, o: 0.35, d: 1.2 },
    { t: "HA!", x: "84%", y: "14%", s: 28, r: 12, o: 0.45, d: 0.6 },
    { t: "ha", x: "72%", y: "78%", s: 40, r: -8, o: 0.3, d: 1.8 },
    { t: "HA", x: "44%", y: "8%", s: 20, r: 6, o: 0.35, d: 2.4 },
    { t: "haha", x: "90%", y: "52%", s: 24, r: -16, o: 0.4, d: 0.9 },
  ];
  return (
    <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
      {has.map((h, i) => (
        <span
          key={i}
          className="absolute select-none font-bold animate-ha-float"
          style={{
            ...display,
            left: h.x,
            top: h.y,
            fontSize: h.s,
            color: "#FFFFFF",
            opacity: h.o,
            transform: `rotate(${h.r}deg)`,
            animationDelay: `${h.d}s`,
          }}
        >
          {h.t}
        </span>
      ))}
      <style>{`
        @keyframes ha-float {
          0%, 100% { translate: 0 0; }
          50% { translate: 0 -14px; }
        }
        .animate-ha-float { animation: ha-float 5s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .animate-ha-float { animation: none; }
        }
      `}</style>
    </div>
  );
}

/** Official-style App Store badge (text version). Swap the inner content for
 *  Apple's official SVG badge asset when you download it from
 *  developer.apple.com/app-store/marketing/guidelines. */
export function AppStoreBadge({ href }: { href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2.5 rounded-xl bg-[#241633] text-white px-5 py-2.5 transition-transform hover:scale-[1.03] active:scale-[0.98]"
    >
      <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden>
        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
      </svg>
      <span className="text-left leading-tight">
        <span className="block text-[10px] opacity-80">Download on the</span>
        <span className="block text-[15px] font-semibold -mt-0.5">App Store</span>
      </span>
    </a>
  );
}
